import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Building2, Hash, BarChart3, FolderKanban, Settings, Briefcase, ListChecks,
  CalendarCheck, FileText, Clock, Calendar, Download, Loader2, CalendarDays,
} from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminAllReceipts from './AdminAllReceipts';
import UserManagement from './UserManagement';
import CostCenterManagement from './CostCenterManagement';
import GlCodeManagement from './GlCodeManagement';
import ProjectManagement from './ProjectManagement';
import ProjectEditPage from './ProjectEditPage';
import HolidayManagement from './HolidayManagement';
import TareasManagement from './TareasManagement';
import AdminSettings from './AdminSettings';
import AdminPtoQueue from './AdminPtoQueue';
import PtoLimitsByRole from './PtoLimitsByRole';
import AdminPtoStats from './AdminPtoStats';
import AuditLogViewer from './AuditLogViewer';
import AdminCalendarPage from '../../vacation/pages/AdminCalendarPage';
import SupervisorDetailPage from '../supervisor/SupervisorDetailPage';
import RoleSummaryTab from '../shared/RoleSummaryTab';
import PortalLayout from '../../shared/components/PortalLayout';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle, selectStyle } from '../../styles/tokens';
import client from '../../api/client';

/* ── Tablas sub-tabs ─────────────────────────────────────────────────── */

const TABLAS_TABS = [
  { key: 'users',        label: 'Usuarios',    icon: Users },
  { key: 'employees',    label: 'Empleados',   icon: Briefcase },
  { key: 'projects',     label: 'Proyectos',   icon: FolderKanban },
  { key: 'tareas',       label: 'Tareas',      icon: ListChecks },
  { key: 'cost-centers', label: 'Códigos',     icon: Building2 },
  { key: 'gl-codes',     label: 'Códigos GL',  icon: Hash },
  { key: 'holidays',     label: 'Feriados',    icon: CalendarDays },
  { key: 'pto-limits',  label: 'Límites PTO',  icon: CalendarCheck },
];

/* ── Empleados tab (adapted from PersonasPage) ─────────────────────── */

import PersonasPage from '../../timesheet/pages/PersonasPage';

/* ── Dashboard sub-tabs ────────────────────────────────────────────────── */

const DASH_TABS = [
  { key: 'gastos',     label: 'Gastos',      icon: FileText },
  { key: 'vacaciones', label: 'Vacaciones',  icon: CalendarCheck },
  { key: 'nomina',     label: 'Nómina',      icon: Clock },
];

/* ── Platform Dashboard (route: /admin) ──────────────────────────────── */

function PlatformDashboard() {
  const [tab, setTab] = useState('gastos');

  return (
    <>
      {/* Sub-tab bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px',
        alignItems: 'stretch', overflowX: 'auto',
      }}>
        {DASH_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 20px', fontSize: 13,
            fontWeight: tab === key ? '700' : '500',
            color: tab === key ? AMBER : '#64748b',
            borderBottom: tab === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'gastos' && <GastosDashboard />}
      {tab === 'vacaciones' && <VacacionesDashboard />}
      {tab === 'nomina' && <NominaDashboard />}
    </>
  );
}

/* ── Gastos Dashboard ─────────────────────────────────────────────────── */

function GastosDashboard() {
  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <AdminDashboard />
    </div>
  );
}

/* ── Vacaciones Dashboard ─────────────────────────────────────────────── */

function VacacionesDashboard() {
  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* PTO Stats */}
      <div style={{ marginBottom: 24 }}>
        <AdminPtoStats />
      </div>

      {/* PTO Queue */}
      <div style={{ marginBottom: 24 }}>
        <AdminPtoQueue />
      </div>

      {/* Calendar */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Calendar size={16} /> Calendario de Vacaciones
        </h2>
        <AdminCalendarPage />
      </div>
    </div>
  );
}

/* ── Nómina Dashboard — renders the same NominaHomePage all roles see ── */

function NominaDashboard() {
  return <RoleSummaryTab initialTab="nomina" hideTabs />;
}

/* ── Tablas Section (route: /admin/tablas) — has its own sub-tab bar ── */

function TablasSection() {
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    return location.state?.tab || 'users';
  });

  // Update tab when navigating back with state
  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);
  }, [location.state?.tab]);

  return (
    <>
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px',
        alignItems: 'stretch', overflowX: 'auto',
      }}>
        {TABLAS_TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 16px', fontSize: 13,
            fontWeight: tab === key ? '700' : '500',
            color: tab === key ? AMBER : '#64748b',
            borderBottom: tab === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === 'users' && <UserManagement />}
      {tab === 'employees' && <PersonasPage />}
      {tab === 'projects' && <ProjectManagement />}
      {tab === 'tareas' && <TareasManagement />}
      {tab === 'cost-centers' && <CostCenterManagement />}
      {tab === 'gl-codes' && <GlCodeManagement />}
      {tab === 'holidays' && <HolidayManagement />}
      {tab === 'pto-limits' && <PtoLimitsByRole />}
    </>
  );
}

/* ── Configuración Section (route: /admin/configuracion) ─────────────── */

function ConfiguracionSection() {
  return (
    <div>
      <AdminSettings />
    </div>
  );
}

/* ── Auditoría Section (route: /admin/auditoria) ──────────────────────── */

function AuditoriaSection() {
  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: '#1e293b', margin: '0 0 16px' }}>Auditoría</h2>
      <AuditLogViewer />
    </div>
  );
}

/* ── Main AdminPortal (Administración hub) ───────────────────────────── */

export default function AdminPortal() {
  const location = useLocation();

  let activeModule = 'dashboard';
  if (location.pathname.startsWith('/admin/tablas') || location.pathname.startsWith('/admin/configuracion') || location.pathname.startsWith('/admin/proyecto') || location.pathname.startsWith('/admin/auditoria')) activeModule = 'configuracion';
  else if (location.pathname.startsWith('/admin/recibos') || location.pathname.startsWith('/admin/receipt')) activeModule = 'gastos';

  return (
    <PortalLayout activeModule={activeModule}>
      <Routes>
        <Route index element={<PlatformDashboard />} />
        <Route path="tablas" element={<TablasSection />} />
        <Route path="configuracion" element={<ConfiguracionSection />} />
        <Route path="auditoria" element={<AuditoriaSection />} />
        <Route path="recibos" element={<AdminAllReceipts />} />
        <Route path="receipt/:id" element={<SupervisorDetailPage adminMode />} />
        <Route path="proyecto/:projectId" element={<ProjectEditPage />} />
      </Routes>
    </PortalLayout>
  );
}
